<template>
  <page-view :menu="menu" class="managementPageClass">
    <template #header>
      <base-header :title="$t('message.pages.users.title')"> </base-header>
    </template>

    <template #content>
      <v-row no-gutters>
        <user-add />
        <user-edit />
        <user-remove-confirm />
        <permissions-edit />
        <warning-delete-role />
        <warning-max-users />
        <warning-trial-plan />
        <v-col cols="12">
          <v-row align="center" no-gutters justify="space-between">
            <span class="pageTitle">
              {{ $t('message.pages.users.title') }}
            </span>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-on="on"
                  v-bind="attrs"
                  icon
                  small
                  class="pa-0 intro__btn"
                  @click="handleKnowMore"
                >
                  <v-icon>mdi-help</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('message.knowMore') }}</span>
            </v-tooltip>
          </v-row>
        </v-col>
        <v-col cols="12" class="mt-6">
          <v-row
            align="center"
            no-gutters
            justify="space-between"
            class="container__action-tab"
          >
            <v-col v-if="hasEditUserPerm">
              <v-btn
                v-if="isTrial"
                class="mr-2"
                :ripple="false"
                color="button"
                width="200"
                :disabled="loadings.fetch"
                @click="warningAddUserInTrial"
              >
                <span class="white--text">
                  {{ $t('message.pages.users.actions.addUser') }}
                </span>
              </v-btn>
              <v-btn
                v-else
                class="mr-2"
                color="button"
                elevation="0"
                width="200"
                :disabled="loadings.fetch"
                @click="handleCreateUser"
              >
                <span class="white--text">
                  {{ $t('message.pages.users.actions.addUser') }}
                </span>
              </v-btn>
              <v-btn
                v-if="!selectedUsers.length"
                class="mr-2"
                outlined
                color="primary"
                elevation="0"
                :disabled="
                  loadings.fetch || !roles.length || !permissionsTree.length
                "
                @click="handleManagePermissions"
              >
                {{ $t('message.pages.users.actions.permissions') }}
              </v-btn>
              <v-btn
                v-if="selectedUsers.length"
                color="red"
                elevation="0"
                dark
                :disabled="loadings.fetch"
                @click="handleRemoveSelectedUsers"
              >
                {{ $t('message.pages.users.actions.deleteUser') }}
              </v-btn>
            </v-col>
            <v-col v-else></v-col>
            <v-text-field
              v-model="searchUserText"
              class="align-self-end"
              dense
              outlined
              :label="$t('message.pages.users.search')"
              prepend-inner-icon="mdi-magnify"
              hide-details
              @input="filterUsers"
              style="max-width: 260px"
            ></v-text-field>
          </v-row>
        </v-col>
        <v-col class="mt-7" cols="12">
          <v-row no-gutters>
            <v-col cols="12" class="pa-0 ml-2 pb-4">
              <v-row no-gutters class="container__management-list">
                <div class="d-flex">
                  <v-card
                    class="mt-1"
                    flat
                    width="100%"
                    height="45px"
                    v-if="!loadings.fetch"
                  >
                    <v-row no-gutters class="pointer">
                      <v-card
                        class="d-flex align-center justify-center"
                        height="45px"
                        flat
                        width="57.16px"
                      >
                        <v-simple-checkbox
                          v-if="hasEditUserPerm"
                          :color="!checkboxIndeterminateStatus ? '#3F55EE' : ''"
                          :indeterminate="checkboxIndeterminateStatus"
                          :ripple="false"
                          :value="anyItemSelected"
                          @input="selectAllItems"
                        />
                      </v-card>
                      <v-col
                        class="d-flex align-center justify-start"
                        style="padding-left: 5px"
                      >
                        <v-row no-gutters>
                          <span
                            class="tableHeadersText mt-1"
                            style="
                              height: 24px;
                              color: var(--v-blueMarine-base);
                            "
                          >
                            {{ $t('message.pages.users.table.user') }}
                          </span>
                        </v-row>
                      </v-col>
                      <v-card
                        class="d-flex align-center"
                        height="45px"
                        flat
                        width="150px"
                        style="padding-left: 5px"
                      >
                        <v-hover v-slot="{ hover }">
                          <v-row no-gutters @click="handleOrderByPerm">
                            <span
                              class="tableHeadersText mt-1"
                              style="
                                height: 24px;
                                color: var(--v-blueMarine-base);
                              "
                            >
                              {{ $t('message.pages.users.table.permission') }}
                            </span>
                            <v-icon v-if="hover && !permOrder"
                              >mdi-menu-down</v-icon
                            >
                            <v-icon v-if="hover && permOrder === 'desc'"
                              >mdi-menu-down</v-icon
                            >
                            <v-icon v-if="hover && permOrder === 'asc'"
                              >mdi-menu-up</v-icon
                            >
                          </v-row>
                        </v-hover>
                      </v-card>
                      <v-card
                        class="d-flex align-center"
                        height="45px"
                        flat
                        width="158"
                      >
                        <v-hover v-slot="{ hover }">
                          <v-row no-gutters @click="handleOrderByLastModified">
                            <span
                              class="tableHeadersText mt-1"
                              style="
                                height: 24px;
                                color: var(--v-blueMarine-base);
                              "
                            >
                              {{ $t('message.lastModification') }}
                            </span>
                            <v-icon v-if="hover && !lastModifiedOrder"
                              >mdi-menu-down</v-icon
                            >
                            <v-icon v-if="hover && lastModifiedOrder === 'desc'"
                              >mdi-menu-down</v-icon
                            >
                            <v-icon v-if="hover && lastModifiedOrder === 'asc'"
                              >mdi-menu-up</v-icon
                            >
                          </v-row>
                        </v-hover>
                      </v-card>
                    </v-row>
                  </v-card>
                </div>
                <div class="d-flex">
                  <v-col cols="12" v-if="loadings.fetch">
                    <v-skeleton-loader
                      class="skelLoaderMargin"
                      type="table-tbody"
                    />
                  </v-col>
                  <vue-virtual-table
                    v-else-if="usersList.length"
                    class="usersManagementTable data-table__content"
                    :config="headers"
                    :data="
                      searchUserText.length > 0 ? filteredUsersList : usersList
                    "
                    :height="tableHeight"
                    :itemHeight="48"
                    :minWidth="450"
                    @contextmenu="handleShowMenu"
                  >
                    <template slot-scope="scope" slot="selectCommon">
                      <v-row
                        class="pt-1"
                        no-gutters
                        justify="center"
                        align="center"
                      >
                        <v-simple-checkbox
                          v-if="
                            !scope.row.is_root &&
                            hasEditUserPerm &&
                            hasHigherHierarchy(scope.row)
                          "
                          color="#3F55EE"
                          :ripple="false"
                          :value="checkIfSelected(scope.row)"
                          @input="checkboxSelection(scope.row)"
                        ></v-simple-checkbox>
                        <v-card flat width="100%" height="100%" />
                      </v-row>
                    </template>
                    <template slot-scope="scope" slot="userCommon">
                      <v-col class="d-flex align-center justify-start pa-0">
                        <span
                          :title="scope.row.user"
                          class="text-truncate"
                          :style="'max-width: 100%'"
                        >
                          {{
                            scope.row.user ? scope.row.user : scope.row.email
                          }}
                        </span>
                      </v-col>
                    </template>
                    <template slot-scope="scope" slot="email">
                      <v-row no-gutters>
                        <span>{{ scope.row.email }}</span>
                      </v-row>
                    </template>
                    <template slot-scope="scope" slot="permission">
                      <v-row no-gutters>
                        <span v-if="scope.row.role_name">
                          {{ capitalizeFirstLetter(scope.row.role_name) }}
                        </span>
                      </v-row>
                    </template>
                    <template slot-scope="scope" slot="lastAccess">
                      <v-row no-gutters>
                        <span>
                          {{
                            new Date(scope.row.updated_at)
                              .toLocaleString('pt-BR')
                              .slice(0, 3) +
                            new Date(scope.row.updated_at)
                              .toLocaleString(getLocale(), { month: 'long' })
                              .slice(0, 3) +
                            '/' +
                            new Date(scope.row.updated_at)
                              .toLocaleString('pt-BR')
                              .slice(6, 10)
                          }}
                        </span>
                      </v-row>
                    </template>
                  </vue-virtual-table>
                </div>
                <v-card
                  v-if="!usersList.length"
                  class="d-flex flex-row align-center justify-center"
                  width="100%"
                  height="316px"
                  flat
                >
                  <v-icon class="mb-1 mr-1" color="primary">
                    mdi-folder-search-outline
                  </v-icon>
                  <span class="subtitle-2">
                    {{ $t('message.pages.users.table.noFoundUser') }}
                  </span>
                </v-card>
                <v-menu
                  v-model="showMenu"
                  min-width="300px"
                  :position-x="x"
                  :position-y="y"
                  absolute
                  offset-y
                  :transition="null"
                  v-if="!menuItem.is_root && hasHigherHierarchy(menuItem)"
                >
                  <v-list dense>
                    <v-list-item @click="handleEditUser">
                      <v-list-item-icon>
                        <v-icon>mdi-pencil</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>{{
                        $t('message.edit')
                      }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="handleRemoveFromMenu">
                      <v-list-item-icon>
                        <v-icon>mdi-delete</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>{{
                        $t('message.remove')
                      }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-row>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </template>
  </page-view>
</template>

<script>
// internal imports
import linkHelper from '@/utils/linkHelper.mixin.js'
import Fuse from 'fuse.js'

// API
import subUsersManagement from '@/services/subUsersManagement'
import organizationApi from '@/services/organizationApi'

// Components
import UserAdd from './UserAdd.vue'
import UserEdit from './UserEdit.vue'
import UserRemoveConfirm from './UserRemoveConfirm.vue'
import PermissionsEdit from './PermissionsEdit.vue'
import WarningDeleteRole from './WarningDeleteRole.vue'
import WarningMaxUsers from './WarningMaxUsers.vue'
import WarningTrialPlan from './WarningTrialPlan.vue'

// import LoadingLogo from '@/components/LoadingLogo.vue'
import PageView from '@/layout/PageView.vue'
import VueVirtualTable from 'vue-virtual-table'
import { mapMutations, mapState } from 'vuex'

export default {
  name: 'Management',
  mixins: [linkHelper],
  components: {
    // LoadingLogo,
    PermissionsEdit,
    WarningDeleteRole,
    WarningMaxUsers,
    UserAdd,
    UserEdit,
    UserRemoveConfirm,
    VueVirtualTable,
    PageView,
    BaseHeader: () => import('@/components/BaseHeader.vue'),
    WarningTrialPlan,
  },
  data: () => ({
    permOrder: 'asc',
    lastModifiedOrder: 'asc',
    libraryId: null,
    usersList: [],
    filteredUsersList: [],
    selectedUsers: [],
    loadings: {
      fetchData: false,
      fetch: false,
    },
    errors: {
      fetchData: false,
    },
    errMsg: '',
    errCode: '502',
    fullErrorMsg: '',
    searchUserText: '',
    headers: [
      {
        prop: '_action',
        name: 'action',
        actionName: 'selectCommon',
        width: 50,
        alignItems: 'start',
      },
      {
        prop: '_action',
        name: 'Usuário',
        actionName: 'userCommon',
      },
      {
        prop: '_action',
        name: 'Permissão',
        actionName: 'permission',
        width: 145,
      },
      {
        prop: '_action',
        name: 'Último acesso',
        actionName: 'lastAccess',
        width: 145,
      },
    ],
    noInfoFound: false,
    roles: [],
    permissionsTree: [],
    showMenu: false,
    isTrial: false,
    x: 0,
    y: 0,
    menuItem: false,
  }),
  computed: {
    ...mapState('user', {
      planInfo: 'userPlanInfo',
    }),
    hasEditUserPerm() {
      return (
        this.$store.state.user.userPermissions?.includes('put-user') ?? false
      )
    },
    checkboxIndeterminateStatus() {
      return (
        this.selectedUsers.length > 0 &&
        this.selectedUsers.length < this.usersList.length
      )
    },
    anyItemSelected() {
      return this.selectedUsers.length > 0
    },
    tableHeight() {
      if (this.usersList.length < 5) {
        return 288
      } else if (this.usersList.length <= 100) {
        return (this.usersList.length + 1) * 48 + 5
      } else {
        return 4805
      }
    },
    menu() {
      return this.$store.state.user.settingsNavItems
    },
    checkIfCanCreate() {
      // check if sum of addon + plan users is higher than already created users -1(main account)
      const addonUsers = this.planInfo.addons?.slot_user
        ? this.planInfo.addons?.slot_user[0]?.quantity
        : 0
      const planUsers = this.planInfo.subscription?.metadata?.users ?? 0
      const maxAmountOfUsers = addonUsers + planUsers
      return maxAmountOfUsers > this.usersList.length - 1
    },
  },
  created() {
    this.doUsersSearch()
    this.doRolePermissionsSearch()
    this.rolesSearch()
  },
  mounted() {
    this.checkIfIsTrial()

    this.$root.$on('saveNewUser', (user) => {
      this.usersList.push(user)
    })
    this.$root.$on('updateUser', (updatedUser) => {
      this.usersList = this.usersList.map((user) =>
        user.id === updatedUser.id ? updatedUser : user
      )
    })
    this.$root.$on('removeUser', (user) => {
      this.usersList = this.usersList.filter((item) => item.id != user.id)
      this.selectedUsers = []
    })
    this.$root.$on('managementCreateRole', (role) => {
      this.roles.push(role)
    })
    this.$root.$on('updateRoles', (updatedRole) => {
      this.roles = this.roles.map((role) =>
        role.id === updatedRole.id ? updatedRole : role
      )
    })
    this.$root.$on('managementRemoveRole', (deletingRoleId) => {
      this.roles = this.roles.filter((item) => item.id != deletingRoleId)
    })
  },
  beforeDestroy() {},
  watch: {
    permOrder: function () {
      this.orderByPerm()
    },
    lastModifiedOrder: function () {
      this.orderByLastModified()
    },
  },
  methods: {
    ...mapMutations('user', ['UPDATE_USER_PERMISSIONS']),
    handleOrderByPerm() {
      if (!this.permOrder || this.permOrder === 'desc')
        return (this.permOrder = 'asc')
      if (this.permOrder === 'asc') return (this.permOrder = 'desc')
    },
    handleOrderByLastModified() {
      if (!this.lastModifiedOrder || this.lastModifiedOrder === 'desc')
        return (this.lastModifiedOrder = 'asc')
      if (this.lastModifiedOrder === 'asc')
        return (this.lastModifiedOrder = 'desc')
    },
    checkIfIsTrial() {
      this.isTrial =
        this.planInfo?.subscription?.status === 'trialing' ? true : false
    },
    orderByPerm() {
      if (this.permOrder === 'asc') {
        this.usersList.sort((a, b) =>
          a.role_name.localeCompare(b.role_name, undefined, {
            sensitivity: 'base',
          })
        )
      } else {
        this.usersList.sort((a, b) =>
          b.role_name.localeCompare(a.role_name, undefined, {
            sensitivity: 'base',
          })
        )
      }
    },
    orderByLastModified() {
      if (this.lastModifiedOrder === 'asc') {
        this.usersList.sort(
          (a, b) => new Date(a.updated_at) - new Date(b.updated_at)
        )
      } else {
        this.usersList.sort(
          (a, b) => new Date(b.updated_at) - new Date(a.updated_at)
        )
      }
    },
    hasHigherHierarchy(beingEditedUser) {
      const isAdmin =
        !!this.$store.state.user?.userAllInfo?.role_name?.toLowerCase() ===
        'admin'
      const editingUserRole = beingEditedUser.role_name?.toLowerCase()
      if (this.$store.state.user?.userAllInfo?.is_root) return true
      if (isAdmin) return true
      if (
        this.hasEditUserPerm &&
        editingUserRole != 'admin' &&
        editingUserRole != 'administrador'
      )
        return true
      return false
    },
    filterUsers() {
      let searchValue = this.searchUserText
      if (searchValue && searchValue.length > 0) {
        let list = JSON.parse(JSON.stringify(this.usersList))
        let fuseOptions = {
          keys: ['email'],
          includeScore: true,
          threshold: 0.6,
        }
        const fuse = new Fuse(list, fuseOptions)
        let searchResult = fuse.search(searchValue)
        const itemsResults = searchResult.map((result) => result.item)
        this.filteredUsersList = itemsResults
      } else {
        this.filteredUsersList = []
      }
    },
    capitalizeFirstLetter(val) {
      return val.charAt(0).toUpperCase() + val.slice(1)
    },
    handleEditUser() {
      this.$root.$emit('openUserEdit', {
        user: this.menuItem,
        roles: this.roles,
      })
    },
    handleShowMenu(item, e) {
      this.menuItem = item
      e.preventDefault()
      this.showMenu = false
      this.x = e.clientX
      this.y = e.clientY
      this.$nextTick(() => {
        this.showMenu = true
      })
    },
    async doRolePermissionsSearch() {
      try {
        let ret = await organizationApi.getOrganizationPermissions()
        const permissions = ret.data.permissions
        const permissionsRenames = {
          video: this.$i18next.t('message.videos'),
          'general-config': this.$i18next.t(
            'message.pages.users.categories.qualitiesAndDomains'
          ),
          live: 'Live',
          user: this.$i18next.t('message.pages.users.categories.management'),
          drm: this.$i18next.t('message.pages.drm.watermark'),
          'test-ab': this.$i18next.t('message.pages.AbTest.AbTest'),
          plan: this.$i18next.t('message.pages.users.categories.plan'),
          analytics: 'Analytics',
          role: this.$i18next.t('message.pages.users.categories.roles'),
          permission: this.$i18next.t(
            'message.pages.users.actions.permissions'
          ),
          oauth2: 'Oauth2',
          generics: this.$i18next.t('message.pages.users.categories.generic'),
        }
        const groupedPermissions = permissions.reduce((result, permission) => {
          const groupName = permission.group_path
          if (!result[groupName]) {
            result[groupName] = {
              group: groupName,
              read: [],
              editor: [],
              switch: false,
              renamedGroup: permissionsRenames[groupName] ?? groupName,
              selectValues: [
                {
                  title: this.$i18next.t('message.pages.users.roles.noAccess'),
                  value: false,
                },
              ],
            }
          }
          if (permission.name.includes('get')) {
            result[groupName].selectValues.push({
              title: this.$i18next.t('message.pages.users.roles.viewer'),
              value: 'read',
            })
            result[groupName].read.push(permission)
          }
          if (
            permission.name.includes('post') ||
            permission.name.includes('put') ||
            permission.name.includes('delete')
          ) {
            result[groupName].selectValues.push({
              title: this.$i18next.t('message.pages.users.roles.edit'),
              value: 'editor',
            })
          }
          if (permission.name.includes('view-plan-denied')) {
            result[groupName].plan = [
              {
                ...permission,
              },
            ]
          } else {
            result[groupName].editor.push(permission)
          }

          if (
            groupName === 'video' ||
            groupName === 'user' ||
            groupName === 'role' ||
            groupName === 'permission' ||
            groupName === 'plan'
          ) {
            result[groupName].switch = 'read'
            if (groupName === 'plan') {
              result[groupName].selectValues.push({
                title: this.$i18next.t('message.pages.users.roles.noAccess'),
                value: 'plan',
              })
            }
          }
          if (groupName === 'generics') {
            result[groupName].switch = 'editor'
          }
          if (groupName === 'analytics') {
            result[groupName].switch = false
            result[groupName].selectValues.push({
              title: this.$i18next.t('message.pages.users.roles.noAccess'),
              value: false,
            })
          }
          return result
        }, {})
        const result = Object.values(groupedPermissions)
        this.permissionsTree = result
      } catch (e) {
        console.log('e', e)
      }
    },
    async rolesSearch() {
      const rolesRenames = {
        viewer: this.$i18next.t('message.pages.users.roles.viewer'),
        admin: this.$i18next.t('message.pages.users.roles.admin'),
        editor: this.$i18next.t('message.pages.users.roles.editor'),
      }
      try {
        let ret = await organizationApi.getOrganizationRoles()
        this.roles = ret.data.roles.map((role) => {
          if (
            role.name === 'viewer' ||
            role.name === 'editor' ||
            role.name === 'admin'
          ) {
            return {
              ...role,
              name: rolesRenames[role.name],
              description: this.$i18next.t(
                `message.pages.users.permissionsEdit.${role.name}Description`
              ),
            }
          } else {
            return { ...role }
          }
        })
      } catch (e) {
        console.log('e', e)
      }
    },
    async doUsersSearch() {
      this.loadings.fetch = true
      try {
        let ret = await subUsersManagement.getAllsubUsers()
        this.usersList = ret.data.map((user) => {
          if (user.role_name === 'admin') {
            return {
              ...user,
              role_name: this.$i18next.t('message.pages.users.roles.admin'),
            }
          } else if (user.role_name === 'viewer') {
            return {
              ...user,
              role_name: this.$i18next.t('message.pages.users.roles.viewer'),
            }
          }
          return user
        })
        this.orderUsers()
        this.loadings.fetch = false
      } catch (e) {
        this.loadings.fetch = false
        console.log('e', e)
      }
    },
    orderUsers() {
      this.usersList
        .sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { numeric: true })
        )
        .sort((a, b) => {
          if (a.is_root && !b.is_root) return -1
          if (!a.is_root && b.is_root) return 1
          if (a.is_root && b.is_root) return 0
        })
    },
    async handleCreateUser() {
      if (!this.checkIfCanCreate) {
        this.$root.$emit('modalMaxUsers')
        return
      }
      this.$root.$emit('openUserAdd', this.roles)
    },
    async warningAddUserInTrial() {
      this.$root.$emit('modalWarningTrialPlan')
    },
    handleManagePermissions() {
      this.$root.$emit('openPermissionEdit', {
        users: JSON.parse(JSON.stringify(this.usersList)),
        roles: JSON.parse(JSON.stringify(this.roles)),
        permissions: JSON.parse(JSON.stringify(this.permissionsTree)),
      })
    },
    getLocale() {
      if (this.$i18next.resolvedLanguage === 'en') return 'en-US'
      return 'pt-BR'
    },
    async handleRemoveFromMenu() {
      let removingUsers = []
      removingUsers.push(this.menuItem)
      this.$root.$emit('removeUsersConfirm', removingUsers)
    },
    async handleRemoveSelectedUsers() {
      this.$root.$emit(
        'removeUsersConfirm',
        JSON.parse(JSON.stringify(this.selectedUsers))
      )
    },
    clearErrors() {
      Object.keys(this.errors).forEach((e) => (this.errors[e] = false))
    },
    checkIfSelected(item) {
      let foundItem = this.selectedUsers.find((searchedItem) => {
        return searchedItem.id === item.id
      })
      if (foundItem) return true
      return false
    },
    checkboxSelection(item) {
      let alreadySelected = this.checkIfSelected(item)
      if (alreadySelected) {
        let newArr = [
          ...this.selectedUsers.filter((lookUser) => {
            return lookUser.id !== item.id
          }),
        ]
        this.selectedUsers = [...newArr]
      } else {
        this.selectedUsers.push(item)
      }
    },
    selectAllItems() {
      if (this.selectedUsers.length === this.usersList.length - 1) {
        this.selectedUsers = []
        return
      }
      this.selectedUsers = JSON.parse(JSON.stringify(this.usersList))
      this.selectedUsers.splice(0, 1)
    },
    handleKnowMore() {
      const link = this.getUserManagementLink()
      window.open(link, '_blank')
    },
  },
}
</script>

<style lang="scss">
.pageTitle {
  font-weight: 500;
  font-size: 18px;
  line-height: 21px;
  color: var(--v-brand-darken2);
}
.smallBtnText {
  font-weight: 500 !important;
  font-size: 14px !important;
}
.usersManagementTable .header-cell {
  justify-content: start !important;
  padding-left: 20px !important;
  font-weight: 600;
  font-size: 12px;
  line-height: 20px;
}

.usersManagementTable .item-cell {
  border-bottom: 0px !important;
  border-top: 1px solid var(--v-contrast-base);
}

.usersManagementTable {
  overflow-x: hidden !important;
  background-color: var(--v-dark-newBackground);
}

.usersManagementTable .t-header {
  display: none;
}

.bg-dark .usersManagementTable .item-line {
  background-color: var(--v-neutral-darken2) !important;
}
.main-scroll {
  border: none !important;
}
.tableHeadersText {
  font-weight: 500;
  font-size: 14px;
  line-height: 150%;
}
.skelLoaderMargin .v-skeleton-loader__table-tbody {
  margin: 0px;
  padding: 0px;
}

.bg-dark,
.theme--dark {
  .pageTitle {
    color: #8fb1e3;
  }
}
</style>
<style lang="scss" scoped>
.container__management-list {
  width: 100%;
  overflow-x: auto;
  > div {
    width: 100%;
    min-width: 652px;
  }
}

@media screen and (max-width: 667px) {
  .container__action-tab {
    flex-direction: column !important;
    align-items: start !important;

    > .v-input {
      margin-top: 8px;
    }
  }
}

$size: 1.35rem;

.bg-dark {
  .intro {
    &__btn {
      --intro-background-color: rgba(255, 255, 255, 0.54);
      --intro-foreground-color: #000;
    }
  }
}

.intro {
  &__btn {
    --intro-background-color: var(--v-neutral-lighten1);
    --intro-foreground-color: var(--v-neutral-lighten4);

    position: relative;
    top: 0.5px;

    &::v-deep {
      .v-icon {
        background-color: var(--intro-background-color);
        width: $size;
        height: $size;
        border-radius: 100%;

        &[class*='mdi-'] {
          font-size: $size * 0.8;

          &::before {
            color: var(--intro-foreground-color);
          }
        }
      }
    }
  }
}
</style>
<style>
.managementPageClass > .page-view__nav {
  margin-right: 30px !important;
  @media (min-width: 680px) {
  }
}
</style>
